import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Select, Row, Col, Card } from 'antd';
import { useTranslation } from 'react-i18next';
import WizardProgress from './WizardProgress';
import { get_organizations, get_workspaces, get_modules, get_organization_info } from '../../redux/actions/organizationActions';

const { Option } = Select;

const WizardStart = () => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const dispatch = useDispatch();

	const { user, isAdminUser } = useSelector(state => state.auth);
	const { organizations, organization_info, workspaces, modules } = useSelector(state => state.organization);

	const [selectedOrg, setSelectedOrg] = useState(null);
	const [selectedWorkspace, setSelectedWorkspace] = useState(null);
	const [selectedModule, setSelectedModule] = useState(null);

	useEffect(() => {
		if (user?.id) {
			if (isAdminUser) {
				// Admin users can select any organization
				dispatch(get_organizations());
			} else {
				// Client users get their organization auto-selected and locked
				const userOrganization = user.organization;
				setSelectedOrg(userOrganization);
				dispatch(get_organization_info(userOrganization));
				dispatch(get_workspaces({ organization: userOrganization }));
			}
		}
	}, [dispatch, user, isAdminUser]);

	// Handle organization selection
	const handleOrgChange = useCallback((orgId) => {
		setSelectedOrg(orgId);
		setSelectedWorkspace(null);
		setSelectedModule(null);
		if (orgId) {
			dispatch(get_workspaces({ organization: orgId }));
		}
	}, [dispatch]);

	// Handle workspace selection
	const handleWorkspaceChange = useCallback((workspaceId) => {
		setSelectedWorkspace(workspaceId);
		setSelectedModule(null);
		if (workspaceId && selectedOrg) {
			dispatch(get_modules({ organization: selectedOrg, workspace: workspaceId }));
		}
	}, [dispatch, selectedOrg]);

	const handleContinue = () => {
		if (selectedOrg && selectedWorkspace && selectedModule) {
			// Find complete objects instead of just storing IDs
			const selectedOrgObj = organizations?.find(org => org._id?.$oid === selectedOrg);
			const selectedWorkspaceObj = workspaces?.find(ws => ws._id?.$oid === selectedWorkspace);
			const selectedModuleObj = modules?.find(mod => mod._id?.$oid === selectedModule);

			// If selectedOrgObj is not found (client user case), use organization_info
			const orgData = selectedOrgObj || organization_info;

			// Store complete wizard state with full objects
			const wizardState = {
				organization: {
					id: selectedOrg,
					name: orgData?.name || 'Sin información',
					...orgData
				},
				workspace: {
					id: selectedWorkspace,
					name: selectedWorkspaceObj?.name || 'Sin información',
					...selectedWorkspaceObj
				},
				module: {
					id: selectedModule,
					name: selectedModuleObj?.name || 'Sin información',
					...selectedModuleObj
				}
			};
			localStorage.setItem('wizardState', JSON.stringify(wizardState));
			navigate('/wizard/schemas');
		}
	};

	const canContinue = selectedOrg && selectedWorkspace && selectedModule;

	// Filter workspaces based on user type
	const filteredWorkspaces = isAdminUser
		? workspaces
		: workspaces?.filter(workspace => workspace.value === 2);

	return (
		<div className="wizard-container">
			<WizardProgress currentStep={0} />

			<div className="content-container" style={{ padding: '40px', maxWidth: '1200px', margin: '0 auto' }}>
				<Row gutter={[40, 40]} align="middle">
					<Col xs={24} md={12}>
						<div className="welcome-section">
							<h1 style={{ fontSize: '48px', fontWeight: 'bold', marginBottom: '20px', color: '#333' }}>
								¡Bienvenido!
							</h1>
							<p style={{ fontSize: '16px', color: '#666', lineHeight: '1.6', marginBottom: '40px' }}>
								El Self Service te permite realizar cargas masivas de datos organizacionales
								de manera guiada y controlada. Configura esquemas, crea ciclos, sube archivos CSV
								y monitorea el procesamiento de tus datos para actualizar áreas, rutas, tiendas
								y usuarios de forma eficiente.
							</p>

							<div className="selection-form">
								<h3 style={{ marginBottom: '20px', color: '#333' }}>
									Antes de continuar, revisemos tus datos:
								</h3>

								<div style={{ marginBottom: '20px' }}>
									<label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
										ORGANIZACIÓN
									</label>
									<Select
										placeholder="Selecciona una organización"
										style={{ width: '100%', height: '40px' }}
										value={selectedOrg}
										onChange={handleOrgChange}
										disabled={!isAdminUser}
										showSearch
										filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
									>
										{isAdminUser ? (
											organizations?.map(org => (
												<Option key={org?._id?.$oid} value={org?._id?.$oid}>
													{org.name}
												</Option>
											))
										) : (
											<Option key={user?.organization} value={user?.organization}>
												{localStorage.getItem('organization_name') || user?.organization_name || 'Mi Organización'}
											</Option>
										)}
									</Select>
								</div>

								<div style={{ marginBottom: '20px' }}>
									<label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
										ESPACIO DE TRABAJO
									</label>
									<Select
										placeholder="Selecciona un espacio de trabajo"
										style={{ width: '100%', height: '40px' }}
										value={selectedWorkspace}
										onChange={handleWorkspaceChange}
										disabled={!selectedOrg}
										loading={!selectedOrg}
										showSearch
										filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
									>
										{filteredWorkspaces?.map(workspace => (
											<Option key={workspace?._id?.$oid} value={workspace?._id?.$oid}>
												{workspace.name}
											</Option>
										))}
									</Select>
								</div>

								<div style={{ marginBottom: '30px' }}>
									<label style={{ display: 'block', marginBottom: '8px', fontWeight: '500', color: '#333' }}>
										MÓDULO
									</label>
									<Select
										placeholder="Selecciona un módulo"
										style={{ width: '100%', height: '40px' }}
										value={selectedModule}
										onChange={setSelectedModule}
										disabled={!selectedWorkspace}
										loading={!selectedWorkspace}
										showSearch
										filterOption={(input, option) => (option?.children ?? '').toLowerCase().includes(input.toLowerCase())}
									>
										{modules?.map(module => (
											<Option key={module?._id?.$oid} value={module?._id?.$oid}>
												{module.name}
											</Option>
										))}
									</Select>
								</div>

								<Button
									type="primary"
									size="large"
									onClick={handleContinue}
									disabled={!canContinue}
									style={{
										height: '50px',
										fontSize: '16px',
										fontWeight: '600',
										borderRadius: '6px',
										minWidth: '150px',
										backgroundColor: canContinue ? '#3e619b' : '#d9d9d9',
										borderColor: canContinue ? '#3e619b' : '#d9d9d9'
									}}
								>
									CONTINUAR →
								</Button>
							</div>
						</div>
					</Col>

					<Col xs={24} md={12}>
						<div style={{ textAlign: 'center' }}>
							{/* Self Service Illustration */}
							<img
								src="/assets/selfservice-img.svg"
								alt="Self Service Illustration"
								style={{
									width: '550px',
									borderRadius: '8px',
									margin: '0 auto',
									display: 'block'
								}}
							/>
						</div>
					</Col>
				</Row>
			</div>
		</div>
	);
};

export default WizardStart;
